import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ArrowRight, MapPin, Building2, Briefcase } from 'lucide-react';

const RecentJobs = () => {
  const navigate = useNavigate();
  const [recentJobs, setRecentJobs] = useState([]);
  const [cityCounts, setCityCounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [jobsRes, citiesRes] = await Promise.all([
          api.get("/public-jobs"),
          api.get("/city-job-counts")
        ]);
        
        setRecentJobs(Array.isArray(jobsRes.data) ? jobsRes.data.slice(0, 5) : []);
        setCityCounts(Array.isArray(citiesRes.data) ? citiesRes.data : []);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <section className="py-14 bg-white dark:bg-transparent transition-colors duration-300">
      {/* Container with top alignment items-start */}
      <div className="max-w-[1200px] mx-auto px-4 grid lg:grid-cols-3 gap-8 items-start">
        
        {/* Recent Jobs Section (Takes 2 Columns) */}
        <div className="lg:col-span-2 w-full h-full flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">Recent Jobs</h2>
            <a href="/job-listing" className="text-sm text-blue-600 font-bold hover:underline flex items-center gap-1 transition-colors">
              View All <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          
          <div className="space-y-4">
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map(n => (
                  <div key={n} className="h-[104px] bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
                ))}
              </div>
            ) : (Array.isArray(recentJobs) && recentJobs.length > 0) ? (
              recentJobs.map((job) => {
                const employerName = job.employer?.company_name || job.employer?.name || "Confidential Employer";
                const locationText = job.city ? (job.locality ? `${job.locality}, ${job.city}` : job.city) : (job.location || 'Not Specified');
                const expText = job.freshers_only ? "Fresher" : (job.experience || "Not Specified");

                return (
                  <div
                    key={job.id}
                    onClick={() => navigate(`/job-details/${job.id}`)}
                    className="group p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800/50 hover:border-[#FED547] dark:hover:border-[#FED547] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col sm:flex-row sm:items-center gap-4"
                  >
                    {/* Avatar Icon */}
                    <div className="shrink-0 w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-700 flex items-center justify-center text-gray-400 group-hover:bg-[#FED547] group-hover:text-black transition-colors">
                      <Briefcase className="w-6 h-6" />
                    </div>
                    
                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[17px] text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors truncate leading-tight">
                        {job.title}
                      </h3>
                      
                      <div className="flex items-center text-[13px] font-semibold text-gray-500 dark:text-gray-400 mt-1 mb-2.5 truncate">
                        <Building2 className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                        {employerName}
                      </div>

                      {/* Info Grid (Salary, Location, Exp) */}
                      <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-[13px] text-gray-700 dark:text-gray-300 font-medium">
                        <span className="flex items-center">
                          <span className="w-4 h-4 mr-1.5 text-gray-400 flex items-center justify-center font-bold text-base">₹</span>
                          {job.salary_range || 'Not Disclosed'}
                        </span>
                        <span className="flex items-center">
                          <MapPin className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                          <span className="truncate max-w-[120px]" title={locationText}>{locationText}</span>
                        </span>
                        <span className="flex items-center">
                          <Briefcase className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                          {expText}
                        </span>
                      </div>
                      
                      {/* Job Type Pills */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] font-bold rounded-md uppercase tracking-wider">
                          {job.job_type || 'Full-Time'}
                        </span>
                        {job.work_location_type && (
                          <span className="px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] font-bold rounded-md uppercase tracking-wider">
                            {job.work_location_type}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right Side Apply Action */}
                    <div className="hidden sm:flex shrink-0 items-center justify-center">
                       <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center group-hover:bg-[#FED547] group-hover:border-[#FED547] text-gray-400 group-hover:text-black transition-all duration-300">
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                       </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center border border-gray-100 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <p className="text-gray-500 font-medium">No recent jobs found.</p>
              </div>
            )}
          </div>
        </div>

        {/* Jobs in Top Cities Section (Takes 1 Column) */}
        <div className="w-full h-full flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">Top Cities</h2>
          </div>
          
          <div className="rounded-3xl border border-gray-200 bg-gradient-to-b from-[#FED547]/10 to-white dark:border-gray-800 dark:bg-gray-800/50 dark:from-gray-800/80 dark:to-gray-900 p-6 shadow-sm relative overflow-hidden flex-1">
            {/* Subtle glow effect behind */}
            <div className="absolute top-0 right-0 -mt-6 -mr-6 w-32 h-32 bg-[#FED547]/20 rounded-full blur-3xl"></div>

            <div className="space-y-3 relative z-10">
              {(Array.isArray(cityCounts) ? cityCounts : []).map((c, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/job-listing?city=${encodeURIComponent(c.location)}`)}
                  className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl bg-white dark:bg-gray-800 hover:bg-[#FED547] hover:shadow-lg border border-gray-100 dark:border-gray-700 hover:border-transparent transition-all duration-300 group hover:-translate-y-1 cursor-pointer"
                >
                  <span className="flex items-center gap-3 text-[14px] font-bold text-gray-700 dark:text-gray-300 group-hover:text-black transition-colors">
                    <div className="w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center group-hover:bg-white/40 transition-colors">
                      <MapPin className="w-4 h-4 text-gray-400 group-hover:text-black" />
                    </div>
                    {c.location}
                  </span>
                  <span className="text-[11px] font-extrabold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 group-hover:bg-white group-hover:text-black px-3 py-1.5 rounded-lg transition-colors">
                    {c.count} Jobs
                  </span>
                </button>
              ))}
            </div>
            
            <button 
              onClick={() => navigate('/job-listing')} 
              className="mt-6 w-full flex items-center justify-center gap-2 text-[13px] font-extrabold text-gray-600 hover:text-black dark:text-gray-400 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 py-3.5 rounded-xl transition-all cursor-pointer group"
            >
              Explore All Locations <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};

export default RecentJobs;